import axiosInstance from './axiosInstance'

// ── Enums / constants ─────────────────────────────────────────────────────────

export const DEAL_STATUSES = ['OPEN', 'WON', 'LOST', 'SALES'] as const
export type  DealStatus = typeof DEAL_STATUSES[number]

export const DEAL_STATUS_LABELS: Record<DealStatus, string> = { OPEN: 'Open', WON: 'Won', LOST: 'Lost', SALES: 'Sales' }

export const DEAL_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as const
export type  DealPriority = typeof DEAL_PRIORITIES[number]
export const DEAL_PRIORITY_LABELS: Record<DealPriority, string> = {
  LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High', URGENT: 'Urgent'
}

export const DEAL_SOURCES = ['WEBSITE', 'REFERRAL', 'COLD_CALL', 'EMAIL', 'SOCIAL_MEDIA', 'EXHIBITION', 'OTHER'] as const
export type  DealSource = typeof DEAL_SOURCES[number]
export const DEAL_SOURCE_LABELS: Record<DealSource, string> = {
  WEBSITE: 'Website', REFERRAL: 'Referral', COLD_CALL: 'Cold Call',
  EMAIL: 'Email', SOCIAL_MEDIA: 'Social Media', EXHIBITION: 'Exhibition', OTHER: 'Other',
}

export const TASK_STATUSES = ['OPEN', 'IN_PROGRESS', 'DONE'] as const
export type  TaskStatus = typeof TASK_STATUSES[number]

// ── Sub-types ─────────────────────────────────────────────────────────────────

export interface DealOwner {
  id: string; name: string; email: string; avatarUrl: string | null; roleName: string | null
}
export interface DealCustomer {
  id: string; name: string; companyName: string | null; phone: string
  city?: string | null; address?: string | null; pincode?: string | null; gst?: string | null
}
export interface DealLead {
  id: string; name: string; phone: string
  customerTypeId:   number | null
  customerTypeName: string | null
}
export interface DealContact {
  id: string; name: string; phone: string; role: string | null
}
export interface DealPipelineInfo {
  id: string; name: string
}

// ── Deal ──────────────────────────────────────────────────────────────────────

export interface Deal {
  id:                string
  orgId:             string | null
  lobId:             string | null
  warehouseId:       string | null
  orgName:           string | null
  lobName:           string | null
  warehouseName:     string | null
  customerId:        string | null
  customer:          DealCustomer | null
  leadId:            string | null
  lead:              DealLead | null
  contactId:         string | null
  contact:           DealContact | null
  pipelineId:        string | null
  pipeline:          DealPipelineInfo | null
  owner:             DealOwner
  assignedToId:      string | null
  assignedTo:        DealOwner | null
  createdBy:         DealOwner | null
  updatedBy:         DealOwner | null
  title:             string
  stage:             string
  amount:            number | null
  currency:          string
  probabilityPct:    number | null
  priority:          DealPriority
  source:            string | null
  description:       string | null
  expectedCloseDate: string | null
  status:            DealStatus
  isWon:             boolean
  isLost:            boolean
  lostReason:        string | null
  closedAt:          string | null
  createdAt:         string
  updatedAt:         string
  noteCount:         number
  taskCount:         number
  productCount:      number
  customerTypeId:    number | null
  customerTypeName:  string | null
  requirements?:     string | null
  helpAssigneeId?:   string | null
  helpAssigneeName?: string | null
  helpAssignee?:     DealOwner | null
}

// ── Pipeline Stage ────────────────────────────────────────────────────────────

export interface PipelineStage {
  id:              string
  name:            string
  position:        number
  color:           string
  probabilityPct:  number | null
  isWon:           boolean
  isLost:          boolean
  pipelineId:      string | null
  autoTaskTitle:   string | null
  autoTaskDueDays: number | null
}

// ── Deal Note ─────────────────────────────────────────────────────────────────

export interface DealNote {
  id:        string
  dealId:    string
  content:   string
  createdBy: DealOwner | null
  createdAt: string
  updatedAt: string
}

// ── Deal Task ─────────────────────────────────────────────────────────────────

export interface DealTask {
  id:          string
  dealId:      string
  title:       string
  description: string | null
  dueDate:     string | null
  status:      TaskStatus
  priority:    DealPriority
  assignedTo:  DealOwner | null
  createdBy:   DealOwner | null
  completedAt: string | null
  createdAt:   string
  updatedAt:   string
}

// ── Deal Activity ─────────────────────────────────────────────────────────────

export interface DealActivity {
  id:           string
  dealId:       string
  activityType: string
  description:  string
  metadata:     string | null
  createdBy:    DealOwner | null
  createdAt:    string
}

// ── Payloads ──────────────────────────────────────────────────────────────────

export interface CreateDealPayload {
  title:               string
  customerId?:         string
  leadId?:             string
  contactId?:          string
  customerTypeId?:     number
  pipelineId?:         string
  stage?:              string
  amount?:             number
  currency?:           string
  probabilityPct?:     number
  priority?:            string
  source?:             string
  description?:        string
  expectedCloseDate?:  string
  ownerId?:            string
  orgId?:              string
  lobId?:              string
  warehouseId?:        string
  requirements?:       string | null
}

export interface AddDealActivityPayload {
  type:          string
  description:   string
  createdById:   string
  metadataJson?: string | null
}

export interface UpdateDealPayload {
  title:               string
  customerId?:         string | null
  contactId?:          string | null
  stage:               string
  amount?:             number | null
  currency?:           string
  probabilityPct?:     number | null
  priority?:           string
  source?:             string | null
  description?:        string | null
  expectedCloseDate?:  string | null
  ownerId?:            string
  assignedToId?:       string | null
  requirements?:       string | null
}

export interface DealFilters {
  search?:       string
  statuses?:     DealStatus[]
  stages?:       string[]
  priorities?:   string[]
  sources?:      string[]
  ownerIds?:     string[]
  assignedToIds?: string[]
  customerId?:   string
  contactId?:    string
  pipelineId?:   string
  orgIds?:       string[]
  lobIds?:       string[]
  warehouseIds?: string[]
  fromDate?:     string
  toDate?:       string
  // Price filters
  amountLt?:    number
  amountGt?:    number
  amountEq?:    number
  amountLte?:   number
  amountGte?:   number
  customerTypeIds?: number[]
  page?:         number
  pageSize?:     number
}

export interface PaginatedDeals {
  items:      Deal[]
  total:      number
  page:       number
  limit:      number
  totalPages: number
}

export interface CreateTaskPayload {
  title:         string
  description?:  string
  dueDate?:      string
  priority?:     string
  assignedToId?: string
}

export interface UpdateTaskPayload {
  title:         string
  description?:  string | null
  dueDate?:      string | null
  priority?:     string
  status?:       string
  assignedToId?: string | null
}

// ── API ───────────────────────────────────────────────────────────────────────

export const dealsApi = {

  // ── Deal CRUD ───────────────────────────────────────────────────────────────

  export: async (filters: DealFilters = {}): Promise<void> => {
    const res = await axiosInstance.get('/deals/export', { params: filters, responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([res.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'Deals.xlsx')
    document.body.appendChild(link)
    link.click()
    link.remove()
  },

  getAll: async (filters: DealFilters = {}): Promise<PaginatedDeals> => {
    const res = await axiosInstance.get<{ data: PaginatedDeals }>('/deals', { params: filters })
    return res.data.data
  },

  /** All distinct, non-empty source values in the Deals table (tenant-scoped), sorted A→Z. */
  getSources: async (): Promise<string[]> => {
    const res = await axiosInstance.get<{ data: string[] }>('/deals/sources')
    return res.data.data ?? []
  },

  getById: async (id: string): Promise<Deal> => {
    const res = await axiosInstance.get<{ data: Deal }>('/deals/' + id)
    return res.data.data
  },

  create: async (payload: CreateDealPayload): Promise<Deal> => {
    const res = await axiosInstance.post<{ data: Deal }>('/deals', payload)
    return res.data.data
  },

  update: async (id: string, payload: UpdateDealPayload): Promise<Deal> => {
    const res = await axiosInstance.put<{ data: Deal }>('/deals/' + id, payload)
    return res.data.data
  },

  updateRequirements: async (id: string, requirements: string | null): Promise<Deal> => {
    const res = await axiosInstance.patch<{ data: Deal }>(`/deals/${id}/requirements`, { requirements })
    return res.data.data
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete('/deals/' + id)
  },

  changeStatus: async (id: string, status: DealStatus): Promise<Deal> => {
    const res = await axiosInstance.patch<{ data: Deal }>('/deals/' + id + '/status', { status })
    return res.data.data
  },

  changeStage: async (id: string, deal: Deal, stage: string, stages: PipelineStage[]): Promise<Deal> => {
    const stageConfig = stages.find(s => s.name === stage)
    const res = await axiosInstance.put<{ data: Deal }>('/deals/' + id, {
      title:             deal.title,
      stage,
      amount:            deal.amount,
      currency:          deal.currency,
      probabilityPct:    stageConfig?.probabilityPct ?? deal.probabilityPct,
      priority:          deal.priority,
      source:            deal.source,
      description:       deal.description,
      expectedCloseDate: deal.expectedCloseDate,
      customerId:        deal.customerId,
      contactId:         deal.contactId,
    })
    return res.data.data
  },

  markLost: async (id: string, lostReason?: string): Promise<Deal> => {
    const res = await axiosInstance.post<{ data: Deal }>('/deals/' + id + '/lost', { lostReason: lostReason ?? null })
    return res.data.data
  },

  // ── Notes ───────────────────────────────────────────────────────────────────

  getNotes: async (dealId: string): Promise<DealNote[]> => {
    const res = await axiosInstance.get<{ data: DealNote[] }>('/deals/' + dealId + '/notes')
    return res.data.data
  },

  addNote: async (dealId: string, content: string): Promise<DealNote[]> => {
    const res = await axiosInstance.post<{ data: DealNote[] }>('/deals/' + dealId + '/notes', { content })
    return res.data.data
  },

  updateNote: async (dealId: string, noteId: string, content: string): Promise<DealNote[]> => {
    const res = await axiosInstance.put<{ data: DealNote[] }>('/deals/' + dealId + '/notes/' + noteId, { content })
    return res.data.data
  },

  deleteNote: async (dealId: string, noteId: string): Promise<void> => {
    await axiosInstance.delete('/deals/' + dealId + '/notes/' + noteId)
  },

  // ── Tasks ────────────────────────────────────────────────────────────────────

  getTasks: async (dealId: string): Promise<DealTask[]> => {
    const res = await axiosInstance.get<{ data: DealTask[] }>('/deals/' + dealId + '/tasks')
    return res.data.data
  },

  addTask: async (dealId: string, payload: CreateTaskPayload): Promise<DealTask[]> => {
    const res = await axiosInstance.post<{ data: DealTask[] }>('/deals/' + dealId + '/tasks', payload)
    return res.data.data
  },

  updateTask: async (dealId: string, taskId: string, payload: UpdateTaskPayload): Promise<DealTask[]> => {
    const res = await axiosInstance.put<{ data: DealTask[] }>('/deals/' + dealId + '/tasks/' + taskId, payload)
    return res.data.data
  },

  deleteTask: async (dealId: string, taskId: string): Promise<void> => {
    await axiosInstance.delete('/deals/' + dealId + '/tasks/' + taskId)
  },

  // ── Timeline ─────────────────────────────────────────────────────────────────

  getTimeline: async (dealId: string): Promise<DealActivity[]> => {
    const res = await axiosInstance.get<{ data: DealActivity[] }>('/deals/' + dealId + '/timeline')
    return res.data.data
  },
  addActivity: async (dealId: string, payload: AddDealActivityPayload): Promise<DealActivity> => {
    const res = await axiosInstance.post<{ data: DealActivity }>(`/deals/${dealId}/activities`, payload)
    return res.data.data
  },
  // ── Pipeline Stages ─────────────────────────────────────────────────────────

  getStages: async (pipelineId?: string, orgId?: string): Promise<PipelineStage[]> => {
    const params: Record<string, string> = {}
    if (pipelineId) params.pipelineId = pipelineId
    if (orgId) params.orgId = orgId
    const res = await axiosInstance.get<{ data: PipelineStage[] }>('/pipeline/stages', {
      params: Object.keys(params).length > 0 ? params : undefined
    })
    const stages = res.data.data || []
    if (stages.length === 0) return []

    const hasWon = stages.some(s => s.isWon)
    const hasLost = stages.some(s => s.isLost)

    const result = [...stages]

    // Ensure Won stage exists
    let wonStage = stages.find(s => s.isWon)
    if (!hasWon) {
      const maxPos = Math.max(...result.map(s => s.position), 0)
      wonStage = {
        id: 'fallback-won-stage-id',
        name: 'Closed Won',
        position: maxPos + 1,
        color: '#10b981',
        probabilityPct: 100,
        isWon: true,
        isLost: false,
        pipelineId: stages[0]?.pipelineId || null,
        autoTaskTitle: null,
        autoTaskDueDays: null
      }
      result.push(wonStage)
    }

    // Ensure Lost stage exists
    if (!hasLost) {
      let lostName = 'Closed Lost'
      if (wonStage) {
        if (wonStage.name.toLowerCase() === 'won') {
          lostName = 'Lost'
        } else if (wonStage.name.toLowerCase() === 'closed won') {
          lostName = 'Closed Lost'
        } else if (wonStage.name.includes('Won')) {
          lostName = wonStage.name.replace('Won', 'Lost')
        } else if (wonStage.name.includes('won')) {
          lostName = wonStage.name.replace('won', 'lost')
        }
      }
      const maxPos = Math.max(...result.map(s => s.position), 0)
      result.push({
        id: 'fallback-lost-stage-id',
        name: lostName,
        position: maxPos + 1,
        color: '#ef4444',
        probabilityPct: 0,
        isWon: false,
        isLost: true,
        pipelineId: stages[0]?.pipelineId || null,
        autoTaskTitle: null,
        autoTaskDueDays: null
      })
    }

    return result
  },

  createStage: async (payload: {
    name: string; position: number; color?: string; probabilityPct?: number
    isWon?: boolean; isLost?: boolean
    autoTaskTitle?: string; autoTaskDueDays?: number
  }): Promise<PipelineStage[]> => {
    const res = await axiosInstance.post<{ data: PipelineStage[] }>('/pipeline/stages', payload)
    return res.data.data
  },

  updateStage: async (id: string, payload: {
    name: string; position: number; color: string; probabilityPct?: number | null
    isWon: boolean; isLost: boolean
    autoTaskTitle?: string | null; autoTaskDueDays?: number | null
  }): Promise<PipelineStage[]> => {
    const res = await axiosInstance.put<{ data: PipelineStage[] }>('/pipeline/stages/' + id, payload)
    return res.data.data
  },

  deleteStage: async (id: string): Promise<void> => {
    await axiosInstance.delete('/pipeline/stages/' + id)
  },

  getSummary: async (params?: DealFilters): Promise<DealsSummary> => {
    const p: Record<string, any> = {}
    if (params?.search) p.search = params.search
    if (params?.customerId) p.customerId = params.customerId
    if (params?.contactId) p.contactId = params.contactId
    if (params?.fromDate) p.fromDate = params.fromDate
    if (params?.toDate) p.toDate = params.toDate
    if (params?.statuses?.length) p.statuses = params.statuses
    if (params?.stages?.length) p.stages = params.stages
    if (params?.priorities?.length) p.priorities = params.priorities
    if (params?.sources?.length) p.sources = params.sources
    if (params?.ownerIds?.length) p.ownerIds = params.ownerIds
    if (params?.orgIds?.length) p.orgIds = params.orgIds
    if (params?.lobIds?.length) p.lobIds = params.lobIds
    if (params?.customerTypeIds?.length) p.customerTypeIds = params.customerTypeIds

    const res = await axiosInstance.get<{ data: DealsSummary }>('/deals/summary', {
      params: p,
      paramsSerializer: { indexes: null },
    })
    return res.data.data
  },

  updateHelpAssignee: async (id: string, helpAssigneeId: string | null): Promise<Deal> => {
    const res = await axiosInstance.patch<{ data: Deal }>(`/deals/${id}/help-assignee`, { helpAssigneeId })
    return res.data.data
  },
}

export interface DealStageSummary {
  stage: string
  count: number
  totalAmount: number
}

export interface DealsSummary {
  totalDeals: number
  newDealsCount: number
  openDealsCount: number
  openPipelineAmount: number
  wonDealsCount: number
  lostDealsCount: number
  totalRevenue?: number
  stageSummaries?: DealStageSummary[]
}
